import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import mermaid from 'mermaid';
import { validateMermaidSyntax, initializeMermaid, parseErrorMessage } from '../utils/mermaidUtils';
import ErrorDisplay from './ErrorDisplay';

const Preview = forwardRef(function Preview({ code, theme, isFullscreen, zoom: externalZoom, previewKey, onCodeChange, onZoomChange }, ref) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [parsedError, setParsedError] = useState(null);
  const [zoom, setZoom] = useState(externalZoom || 1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  const previewRef = useRef();
  const renderTimeoutRef = useRef();
  const containerRef = useRef();
  const svgDimensionsRef = useRef({ width: 0, height: 0 });

  // Apply zoom and position transform to SVG
  const applyTransform = useCallback(() => {
    const svgElement = previewRef.current?.querySelector('svg');
    if (svgElement) {
      svgElement.style.transform = `translate(${position.x}px, ${position.y}px) scale(${zoom})`;
      svgElement.style.transformOrigin = 'top left';
      svgElement.style.transition = isDragging ? 'none' : 'transform 0.2s ease';
    }
  }, [zoom, position, isDragging]);

  // Sync external zoom with internal zoom
  useEffect(() => {
    if (externalZoom !== undefined && externalZoom !== zoom) {
      setZoom(externalZoom);
    }
  }, [externalZoom, zoom]);

  const renderDiagram = useCallback(async () => {
    if (!code || !code.trim()) {
      if (previewRef.current) {
        previewRef.current.innerHTML = '';
      }
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    setParsedError(null);

    try {
      // Initialize mermaid with current theme
      initializeMermaid(theme);
      
      // Validate syntax
      const validation = validateMermaidSyntax(code);
      if (!validation.isValid) {
        const errorInfo = {
          message: validation.error,
          suggestion: validation.suggestion,
          lineNumber: validation.lineNumber
        };
        throw Object.assign(new Error(validation.error), errorInfo);
      }

      if (previewRef.current) {
        const diagramId = 'diagram-' + Date.now();
        
        // Clear previous content
        previewRef.current.innerHTML = '';
        
        // Render diagram
        const { svg } = await mermaid.render(diagramId, code);
        
        // Insert the SVG
        previewRef.current.innerHTML = svg;
        
        // Store SVG dimensions and apply transforms
        const svgElement = previewRef.current.querySelector('svg');
        if (svgElement) {
          svgElement.style.display = 'block';
          svgElement.style.margin = '0 auto';
          svgElement.style.cursor = 'grab';
          
          // Store original dimensions
          const bbox = svgElement.getBBox();
          svgDimensionsRef.current = {
            width: bbox.width || svgElement.viewBox.baseVal.width || 800,
            height: bbox.height || svgElement.viewBox.baseVal.height || 600
          };
          
          applyTransform();
        }
      }
    } catch (err) {
      console.error('Mermaid render error:', err);
      
      // Parse the error for better user experience
      const parsedErrorInfo = parseErrorMessage(err);
      setParsedError(parsedErrorInfo);
      setError(err.message || 'Failed to render diagram');
      
      if (previewRef.current) {
        previewRef.current.innerHTML = '';
      }
    } finally {
      setIsLoading(false);
    }
  }, [code, theme, applyTransform]);

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    const newZoom = Math.min(zoom + 0.2, 3);
    setZoom(newZoom);
    onZoomChange?.(newZoom);
  }, [zoom, onZoomChange]);

  const handleZoomOut = useCallback(() => {
    const newZoom = Math.max(zoom - 0.2, 0.1);
    setZoom(newZoom);
    onZoomChange?.(newZoom);
  }, [zoom, onZoomChange]);

  const handleZoomFit = useCallback(() => {
    if (!containerRef.current || !svgDimensionsRef.current.width) return;
    
    const container = containerRef.current;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const svgWidth = svgDimensionsRef.current.width;
    const svgHeight = svgDimensionsRef.current.height;
    
    // Add padding to ensure diagram doesn't touch edges
    const padding = 40;
    const availableWidth = containerWidth - padding;
    const availableHeight = containerHeight - padding;
    
    // Calculate scale to fit both dimensions
    const scaleX = availableWidth / svgWidth;
    const scaleY = availableHeight / svgHeight;
    const newZoom = Math.min(scaleX, scaleY, 1); // Don't zoom larger than 100%
    
    // Calculate position to center the scaled diagram
    const scaledWidth = svgWidth * newZoom;
    const scaledHeight = svgHeight * newZoom;
    const centerX = (containerWidth - scaledWidth) / 2;
    const centerY = (containerHeight - scaledHeight) / 2;
    
    setZoom(newZoom);
    setPosition({ x: centerX, y: centerY });
    onZoomChange?.(newZoom);
  }, [onZoomChange]);

  const handleZoomActual = useCallback(() => {
    if (!containerRef.current || !svgDimensionsRef.current.width) return;
    
    const container = containerRef.current;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const svgWidth = svgDimensionsRef.current.width;
    const svgHeight = svgDimensionsRef.current.height;
    
    // Center the diagram at 1:1 scale
    const centerX = (containerWidth - svgWidth) / 2;
    const centerY = (containerHeight - svgHeight) / 2;
    
    setZoom(1);
    setPosition({ x: centerX, y: centerY });
    onZoomChange?.(1);
  }, [onZoomChange]);

  // Pan handlers
  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return; // Only left click
    const svgElement = previewRef.current?.querySelector('svg');
    if (!svgElement || !e.target.closest('svg')) return;
    
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    svgElement.style.cursor = 'grabbing';
    e.preventDefault();
  }, [position]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  }, [isDragging, dragStart]);

  // Expose zoom functions to parent component
  useImperativeHandle(ref, () => ({
    handleZoomIn,
    handleZoomOut,
    handleZoomFit,
    handleZoomActual
  }), [handleZoomIn, handleZoomOut, handleZoomFit, handleZoomActual]);

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;
    
    setIsDragging(false);
    const svgElement = previewRef.current?.querySelector('svg');
    if (svgElement) {
      svgElement.style.cursor = 'grab';
    }
  }, [isDragging]);

  // Wheel zoom
  const handleWheel = useCallback((e) => {
    if (!e.ctrlKey && !e.metaKey) return;
    
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom(prev => Math.max(0.1, Math.min(5, prev + delta)));
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === '=') {
        e.preventDefault();
        handleZoomIn();
      } else if ((e.ctrlKey || e.metaKey) && e.key === '-') {
        e.preventDefault();
        handleZoomOut();
      } else if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault();
        handleZoomFit();
      } else if ((e.ctrlKey || e.metaKey) && e.key === '1') {
        e.preventDefault();
        handleZoomActual();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleZoomIn, handleZoomOut, handleZoomFit, handleZoomActual]);

  // Sync external zoom changes
  useEffect(() => {
    if (externalZoom !== undefined) {
      setZoom(externalZoom);
    }
  }, [externalZoom, previewKey]);

  // Apply transform when zoom or position changes
  useEffect(() => {
    applyTransform();
  }, [applyTransform]);

  // Debounced rendering
  useEffect(() => {
    clearTimeout(renderTimeoutRef.current);
    renderTimeoutRef.current = setTimeout(renderDiagram, 500);
    
    return () => clearTimeout(renderTimeoutRef.current);
  }, [renderDiagram]);

  // Re-render when fullscreen changes to ensure proper display
  useEffect(() => {
    if (code && code.trim()) {
      // Small delay to ensure CSS transitions complete
      const timer = setTimeout(() => {
        const svgElement = previewRef.current?.querySelector('svg');
        if (svgElement) {
          svgElement.style.display = 'block';
          svgElement.style.margin = '0 auto';
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isFullscreen, code]);

  const renderContent = () => {
    if (isLoading) {
      return <div className="preview-loading">Rendering diagram...</div>;
    }
    
    if (error) {
      return (
        <ErrorDisplay 
          error={error}
          parsedError={parsedError}
          code={code}
          onCodeChange={onCodeChange}
        />
      );
    }
    
    if (!code || !code.trim()) {
      return <div className="preview-loading">Start typing to see your diagram...</div>;
    }
    
    return null;
  };

  return (
    <div 
      className={`preview-container ${isFullscreen ? 'fullscreen' : ''}`}
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
    >
      <div className="preview-content">
        <div ref={previewRef} id="mermaid-preview" style={{ width: '100%' }} />
        {renderContent()}
      </div>
    </div>
  );
});

Preview.propTypes = {
  code: PropTypes.string.isRequired,
  theme: PropTypes.oneOf(['light', 'dark']).isRequired,
  isFullscreen: PropTypes.bool,
  zoom: PropTypes.number,
  previewKey: PropTypes.number,
  onCodeChange: PropTypes.func.isRequired,
  onZoomChange: PropTypes.func
};

Preview.defaultProps = {
  isFullscreen: false
};

export default Preview;
